// ---------------------------------------------------------------------------
// Analytics Anomaly Alerts — AN-020 Phase 1 (schema only)
// ---------------------------------------------------------------------------
// Story AN-020: "Anomaly alerts can notify operators via dashboard/email/inbox
// with snooze/dismiss/history."
//
// Phase 1 scope (this file): persistence schema for current alert state +
// append-only audit trail. Routes, fan-out, and notifications land in later
// phases per docs/vision_execution/openclaw/AN-020-DECISION-PACKET.md.
//
// Design rules locked here:
// - One row per logical anomaly key in `analytics_anomaly_alerts`.
//   Re-fires update `last_fired_at` + `fire_count`, never create duplicates
//   (enforced by `analytics_anomaly_alerts_tenant_key_uq`).
// - `analytics_anomaly_alert_history` is APPEND-ONLY. Snooze/dismiss/notify
//   transitions append a row; nothing mutates a history row.
// - `dismissedAt IS NULL` = active; non-null = tombstoned.
// - `snoozedUntil` is the operator-controlled mute window; expiry is a
//   read-side filter, not a write-side cron.
// - GDPR erasure: delete by tenant_id (standard cascade).
// ---------------------------------------------------------------------------

import {
  foreignKey,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { notificationDeliveries } from "./notifications";

const tz = { withTimezone: true } as const;

export const analyticsAnomalyAlerts = pgTable(
  "analytics_anomaly_alerts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Stable hash over (tenantId, anomalyType, entityType, entityId,
     * evidenceFingerprint). Same anomaly re-firing reuses this row.
     */
    anomalyKey: text("anomaly_key").notNull(),
    anomalyType: text("anomaly_type").notNull(),
    severity: text("severity").notNull(),
    entityType: text("entity_type").notNull(),
    entityId: text("entity_id").notNull(),
    /**
     * Payload from the detector: message, recommendation, evidence numbers,
     * and the AN-019 explanation when available.
     */
    payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
    firstFiredAt: timestamp("first_fired_at", tz).notNull().defaultNow(),
    lastFiredAt: timestamp("last_fired_at", tz).notNull().defaultNow(),
    fireCount: integer("fire_count").notNull().default(1),
    /** NULL = not snoozed. */
    snoozedUntil: timestamp("snoozed_until", tz),
    snoozedBy: uuid("snoozed_by"),
    /** NULL = active. Non-null = tombstone. */
    dismissedAt: timestamp("dismissed_at", tz),
    dismissedBy: uuid("dismissed_by"),
    /** Closed enum on the route layer; stored as text for forward-compat. */
    dismissedReason: text("dismissed_reason"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("analytics_anomaly_alerts_tenant_key_uq").on(
      table.tenantId,
      table.anomalyKey,
    ),
    uniqueIndex("analytics_anomaly_alerts_tenant_id_uq").on(
      table.tenantId,
      table.id,
    ),
    index("analytics_anomaly_alerts_tenant_active_idx").on(
      table.tenantId,
      table.dismissedAt,
      table.snoozedUntil,
    ),
    index("analytics_anomaly_alerts_tenant_type_idx").on(
      table.tenantId,
      table.anomalyType,
    ),
  ],
);

export const analyticsAnomalyAlertHistory = pgTable(
  "analytics_anomaly_alert_history",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    alertId: uuid("alert_id").notNull(),
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Closed vocabulary: 'fired' | 're_fired' | 'notified' | 'snoozed'
     * | 'unsnoozed' | 'dismissed' | 'reopened'. Stored as text for
     * forward-compat; validated at the route layer.
     */
    event: text("event").notNull(),
    /** NULL for system-emitted transitions. */
    actorUserId: uuid("actor_user_id"),
    /** 'dashboard' | 'email' | 'inbox' for `notified` events. NULL otherwise. */
    channel: text("channel"),
    /** Foreign key to the notifications delivery row when `channel` is set. */
    deliveryId: uuid("delivery_id").references(() => notificationDeliveries.id, {
      onDelete: "restrict",
    }),
    /** Snooze duration, dismiss reason, etc. Append-only metadata. */
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    occurredAt: timestamp("occurred_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("anomaly_alert_history_alert_occurred_idx").on(
      table.alertId,
      table.occurredAt,
    ),
    index("anomaly_alert_history_tenant_occurred_idx").on(
      table.tenantId,
      table.occurredAt,
    ),
    foreignKey({
      name: "anomaly_alert_history_alert_tenant_fk",
      columns: [table.tenantId, table.alertId],
      foreignColumns: [
        analyticsAnomalyAlerts.tenantId,
        analyticsAnomalyAlerts.id,
      ],
    }).onDelete("cascade").onUpdate("cascade"),
  ],
);

/**
 * Closed enum surfaced for route-layer validation. Kept in this module so
 * the schema and the dispatch surface share a single source of truth.
 */
export const ANOMALY_ALERT_HISTORY_EVENTS = [
  "fired",
  "re_fired",
  "notified",
  "snoozed",
  "unsnoozed",
  "dismissed",
  "reopened",
] as const;

export type AnomalyAlertHistoryEvent =
  (typeof ANOMALY_ALERT_HISTORY_EVENTS)[number];

export const ANOMALY_ALERT_DISMISSED_REASONS = [
  "false_positive",
  "expected",
  "fixed",
  "other",
] as const;

export type AnomalyAlertDismissedReason =
  (typeof ANOMALY_ALERT_DISMISSED_REASONS)[number];

export const ANOMALY_ALERT_NOTIFICATION_CHANNELS = [
  "dashboard",
  "email",
  "inbox",
] as const;

export type AnomalyAlertNotificationChannel =
  (typeof ANOMALY_ALERT_NOTIFICATION_CHANNELS)[number];
